/**
 * Voice/video connection manager (one LiveKit room at a time, like Discord).
 *
 * - Guild voice channels: the server authorizes joins and enforces mute/deafen in the
 *   SFU; this module handles devices, playback and reporting our own state.
 * - DM calls: media is end-to-end encrypted (LiveKit E2EE / SFrame) with a key that
 *   every participant derives locally from the DM's MLS group. The key rotates when
 *   the group's membership changes, and never touches the server.
 */
import { create } from 'zustand';
import {
  BaseKeyProvider,
  ConnectionState,
  Room,
  RoomEvent,
  ScreenSharePresets,
  Track,
  VideoPreset,
  createKeyMaterialFromBuffer,
  createLocalAudioTrack,
  isE2EESupported,
  type LocalAudioTrack,
  type LocalTrackPublication,
  type Participant,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RoomOptions,
} from 'livekit-client';
import E2EEWorker from 'livekit-client/e2ee-worker?worker';
import type { StreamQuality, VoiceJoinResponse } from '@comms/shared';
import { api } from './api';
import { getEngine } from './e2ee';
import { createVoiceLeveler } from './leveler';
import { isAndroidApp, nativeCall } from './native';
import { sharedAudioContext } from './sounds';

export interface VoiceUiState {
  status: 'idle' | 'connecting' | 'connected' | 'reconnecting';
  /** guild voice channel, or DM call */
  kind: 'guild' | 'dm' | null;
  channelId: string | null;
  guildId: string | null;
  muted: boolean;
  deafened: boolean;
  camera: boolean;
  screen: boolean;
  /** whose screen share we're watching (server voice channels; DM calls show every stream) */
  watching: string | null;
  /** volume for screen-share audio, 0..1 */
  streamVolume: number;
  /** how loud other people's voices play, 0..2 (above 1 is a boost) */
  callVolume: number;
  /** even out the loudness of our microphone before sending it */
  leveling: boolean;
  /** DM calls: media is end-to-end encrypted */
  encrypted: boolean;
  speaking: string[];
  /** bumps when tracks change so video tiles re-render */
  tracksVersion: number;
  error: string | null;
  /** the browser blocked audio playback until the user interacts (enableAudio) */
  audioBlocked: boolean;
  /** encrypted media is arriving that this device can't decrypt (key out of sync) */
  decryptProblem: boolean;
  /** this device has no usable group state for the call's conversation (can't re-derive the key) */
  keysUnavailable: boolean;
}

const PREF_IN = 'voice.inputDevice';
const PREF_OUT = 'voice.outputDevice';
const PREF_LEVEL = 'voice.leveling';
const PREF_VOLUME = 'voice.callVolume';
function pref(k: string) {
  try {
    return localStorage.getItem(k) ?? undefined;
  } catch {
    return undefined;
  }
}
function savePref(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}
function storedVolume() {
  const v = Number(pref(PREF_VOLUME));
  return Number.isFinite(v) && v >= 0 && v <= 2 && pref(PREF_VOLUME) !== undefined ? v : 1;
}

export const useVoice = create<VoiceUiState>(() => ({
  status: 'idle',
  kind: null,
  channelId: null,
  guildId: null,
  muted: false,
  deafened: false,
  camera: false,
  screen: false,
  watching: null,
  streamVolume: 1,
  callVolume: storedVolume(),
  // Opt-in: on some phones (iPhone Safari in particular) routing the microphone through Web
  // Audio can leave a call silent.
  leveling: pref(PREF_LEVEL) === 'on',
  encrypted: false,
  speaking: [],
  tracksVersion: 0,
  error: null,
  audioBlocked: false,
  decryptProblem: false,
  keysUnavailable: false,
}));

let room: Room | null = null;
let stopKeyUpdates: (() => void) | null = null;
const audioEls = new Map<string, HTMLMediaElement>();

/*
 * The Android app: its web view plays a sound as phone-call audio (earpiece, Bluetooth
 * headset) only if the microphone is already open when the sound starts; otherwise it's
 * media audio, which a phone plays on the loudspeaker whatever the call output is. So in
 * the app the other side's audio waits for our microphone, and the volume boost gets its
 * own audio context, opened during the call.
 */
let playbackReady = true;
let heldAudio: [RemoteTrack, RemoteTrackPublication, Participant][] = [];
let callContext: AudioContext | null = null;
let playbackRestarts = 0;
const MAX_PLAYBACK_RESTARTS = 2;

export const getRoom = () => room;

const IDLE: Partial<VoiceUiState> = {
  status: 'idle',
  kind: null,
  channelId: null,
  guildId: null,
  camera: false,
  screen: false,
  watching: null,
  encrypted: false,
  speaking: [],
  audioBlocked: false,
  decryptProblem: false,
  keysUnavailable: false,
};

/** Guild voice reports self state to the server; DM calls have no server-side state to report. */
function report(patch: Record<string, boolean | string | null>) {
  if (useVoice.getState().kind !== 'guild') return;
  void api('PATCH', '/voice/@me', patch).catch(() => {});
}

function bump() {
  useVoice.setState((s) => ({ tracksVersion: s.tracksVersion + 1 }));
}

function attachAudio(track: RemoteTrack, pub: RemoteTrackPublication, participant: Participant) {
  if (track.kind !== Track.Kind.Audio) return;
  if (!playbackReady) {
    heldAudio.push([track, pub, participant]);
    return;
  }
  const el = track.attach();
  el.dataset.participant = participant.identity;
  el.dataset.source = pub.source;
  el.muted = useVoice.getState().deafened;
  if (pub.source === Track.Source.ScreenShareAudio) el.volume = useVoice.getState().streamVolume;
  document.body.appendChild(el);
  audioEls.set(pub.trackSid, el);
  applyPlayback(pub.trackSid);
}

function removeAudio(sid: string) {
  boosts.get(sid)?.forEach((n) => n.disconnect());
  boosts.delete(sid);
  audioEls.get(sid)?.remove();
  audioEls.delete(sid);
}

function cleanupAudio() {
  for (const sid of [...audioEls.keys()]) removeAudio(sid);
  window.clearTimeout(micRestartTimer);
  watchedMic = null;
  reachedOthers.clear();
  heldAudio = [];
  playbackReady = true;
  void callContext?.close().catch(() => {});
  callContext = null;
}

/** Our microphone is open (or can't be): play the other side's audio now. */
function releaseHeldAudio() {
  playbackReady = true;
  const held = heldAudio;
  heldAudio = [];
  for (const [track, pub, participant] of held) {
    if (pub.track === track) attachAudio(track, pub, participant);
  }
}

/**
 * The Android app says the call isn't playing as phone-call audio: play it again from
 * scratch (a fresh output, now that the microphone is open). False when it's tried enough.
 */
export function restartPlayback(): boolean {
  const r = room;
  if (!r || !playbackReady || audioEls.size === 0) return true;
  if (playbackRestarts >= MAX_PLAYBACK_RESTARTS) return false;
  playbackRestarts++;
  const again: [RemoteTrack, RemoteTrackPublication, Participant][] = [];
  for (const p of r.remoteParticipants.values()) {
    for (const pub of p.audioTrackPublications.values()) {
      if (!pub.track || !audioEls.has(pub.trackSid)) continue;
      pub.track.detach();
      removeAudio(pub.trackSid);
      again.push([pub.track, pub, p]);
    }
  }
  void callContext?.close().catch(() => {});
  callContext = null;
  window.setTimeout(() => {
    if (room !== r) return;
    for (const [track, pub, participant] of again)
      if (pub.track === track) attachAudio(track, pub, participant);
  }, 100);
  return true;
}

/** The audio context call playback goes through when its volume isn't 100%. */
function playbackContext(): AudioContext | null {
  if (!isAndroidApp()) return sharedAudioContext();
  if (!callContext) {
    try {
      callContext = new AudioContext();
    } catch {
      return null;
    }
  }
  if (callContext.state === 'suspended') void callContext.resume().catch(() => {});
  return callContext;
}

/* ------------------------------------------------------- playback volume */

/** Web Audio routes for voices played at other than 100% (element volume can't go above 1). */
const boosts = new Map<string, AudioNode[]>();
let elementVolumeWorks: boolean | null = null;
/** iOS ignores element.volume (always 1): turning a voice down there needs Web Audio too. */
function canSetElementVolume() {
  if (elementVolumeWorks === null) {
    const probe = document.createElement('audio');
    probe.volume = 0.5;
    elementVolumeWorks = probe.volume === 0.5;
  }
  return elementVolumeWorks;
}

function applyPlayback(sid: string) {
  const el = audioEls.get(sid);
  if (!el || el.dataset.source === Track.Source.ScreenShareAudio) return;
  const { callVolume: v, deafened } = useVoice.getState();
  const ctx = v === 1 || (v < 1 && canSetElementVolume()) ? null : playbackContext();
  if (!ctx || ctx.state !== 'running' || !(el.srcObject instanceof MediaStream)) {
    // Plain playback (also the fallback if audio processing isn't available).
    boosts.get(sid)?.forEach((n) => n.disconnect());
    boosts.delete(sid);
    delete el.dataset.boost;
    el.muted = deafened;
    if (canSetElementVolume()) el.volume = Math.min(v, 1);
    return;
  }
  let nodes = boosts.get(sid);
  if (!nodes) {
    const source = ctx.createMediaStreamSource(el.srcObject);
    const gain = ctx.createGain();
    source.connect(gain).connect(ctx.destination);
    nodes = [source, gain];
    boosts.set(sid, nodes);
  }
  // The element keeps playing (muted): Chrome only feeds remote WebRTC audio into Web Audio
  // while it's attached to a playing element.
  el.muted = true;
  el.dataset.boost = String(v);
  (nodes[1] as GainNode).gain.value = deafened ? 0 : v;
}

/** How loud other people's voices play: 0..2 (1 = as received, 2 = twice as loud). */
export function setCallVolume(volume: number) {
  const v = Math.min(2, Math.max(0, volume));
  useVoice.setState({ callVolume: v });
  savePref(PREF_VOLUME, String(v));
  for (const sid of audioEls.keys()) applyPlayback(sid);
}

/* --------------------------------------------------------- voice leveler */

let leveled: LocalAudioTrack | null = null;
let watchingContext = false;

/** Run the microphone through the leveler (see leveler.ts), if it's on and audio allows. */
async function applyLeveler() {
  const track = room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as
    LocalAudioTrack | undefined;
  if (!track) return;
  const want = useVoice.getState().leveling;
  if (!want) {
    if (track.getProcessor()) await track.stopProcessor().catch(() => {});
    return;
  }
  if (track.getProcessor()) return;
  const ctx = sharedAudioContext();
  if (!ctx) return;
  if (ctx.state !== 'running') await ctx.resume().catch(() => {});
  // A suspended context would send silence: rather send the plain microphone.
  if (ctx.state !== 'running') return;
  try {
    track.setAudioContext(ctx);
    const leveler = createVoiceLeveler();
    await track.setProcessor(leveler);
    leveled = track;
    // Never let it silence the call: if the microphone has sound but nothing comes out,
    // send the plain microphone instead.
    void leveler.silencesMicrophone().then((silent) => {
      if (!silent || track.getProcessor() !== leveler) return;
      console.warn('voice leveler produced silence; using the plain microphone');
      void track.stopProcessor().catch(() => {});
    });
    if (!watchingContext) {
      watchingContext = true;
      ctx.addEventListener('statechange', () => {
        const t = leveled;
        if (ctx.state === 'running' || !t?.getProcessor()) return;
        // Interrupted (e.g. a phone call on iOS): fall back to the plain microphone.
        void ctx.resume().then(() => {
          if (ctx.state !== 'running') void t.stopProcessor().catch(() => {});
        });
      });
    }
  } catch (err) {
    console.warn('voice leveler unavailable', err);
    await track.stopProcessor().catch(() => {});
  }
}

export function setLeveling(on: boolean) {
  useVoice.setState({ leveling: on });
  savePref(PREF_LEVEL, on ? 'on' : 'off');
  void applyLeveler();
}

/* ------------------------------------------------------------- E2EE keys */

const KEYRING_SIZE = 16;

/**
 * Key provider fed from the MLS exporter. Each epoch's key goes in its own slot, so
 * frames encrypted under the previous epoch still decrypt while everyone catches up.
 */
class MlsCallKeyProvider extends BaseKeyProvider {
  constructor() {
    super({
      sharedKey: true,
      ratchetWindowSize: 0,
      failureTolerance: -1,
      keyringSize: KEYRING_SIZE,
    });
  }
  async setCallKey(key: Uint8Array, epoch: number) {
    const material = await createKeyMaterialFromBuffer(key.slice().buffer as ArrayBuffer);
    this.onSetEncryptionKey(material, undefined, epoch % KEYRING_SIZE);
  }
}

/**
 * Frames we can't decrypt mean our call key differs from the sender's, usually because
 * one device is behind on the conversation's MLS state. Show it, and re-sync (at most every
 * few seconds) so the key catches up; the warning clears once errors stop.
 */
let refreshKey: (() => Promise<void>) | null = null;
let lastRefresh = 0;
let clearProblem: number | undefined;
function onDecryptError() {
  useVoice.setState({ decryptProblem: true });
  window.clearTimeout(clearProblem);
  clearProblem = window.setTimeout(() => useVoice.setState({ decryptProblem: false }), 8000);
  if (refreshKey && Date.now() - lastRefresh > 5000) {
    lastRefresh = Date.now();
    void refreshKey().catch((err: unknown) => console.warn('call key refresh failed', err));
  }
}

/** Let the browser play call audio (it may block autoplay until you click something). */
export async function enableAudio() {
  await room?.startAudio();
  useVoice.setState({ audioBlocked: !(room?.canPlaybackAudio ?? true) });
}

/* ---------------------------------------------------------- subscriptions */

const isStreamSource = (source: Track.Source) =>
  source === Track.Source.ScreenShare || source === Track.Source.ScreenShareAudio;

/**
 * Server voice channels don't auto-subscribe: voice and cameras always, screen shares only
 * while you watch them, so nobody downloads streams they aren't looking at.
 */
function syncSubscription(pub: RemoteTrackPublication, participant: RemoteParticipant) {
  if (useVoice.getState().kind !== 'guild') return;
  const want = !isStreamSource(pub.source) || useVoice.getState().watching === participant.identity;
  if (pub.isSubscribed !== want) pub.setSubscribed(want);
}

function syncAllSubscriptions() {
  if (!room) return;
  for (const p of room.remoteParticipants.values())
    for (const pub of p.trackPublications.values()) syncSubscription(pub, p);
}

/** Start (or, with null, stop) watching someone's screen share. */
export function watchStream(identity: string | null) {
  if (useVoice.getState().watching === identity) return;
  useVoice.setState({ watching: identity });
  syncAllSubscriptions();
  report({ watchingUserId: identity });
  bump();
}

export function setStreamVolume(volume: number) {
  useVoice.setState({ streamVolume: volume });
  for (const el of audioEls.values())
    if (el.dataset.source === Track.Source.ScreenShareAudio) el.volume = volume;
}

/** Remote participants who are sharing their screen right now. */
export function streamers(): string[] {
  if (!room) return [];
  return [...room.remoteParticipants.values()]
    .filter((p) =>
      [...p.trackPublications.values()].some((pub) => pub.source === Track.Source.ScreenShare),
    )
    .map((p) => p.identity);
}

/* ---------------------------------------------------------------- go live */

export interface GoLiveOptions {
  quality: StreamQuality;
  /** text and detail keeps resolution; motion keeps framerate */
  content: 'detail' | 'motion';
  audio: boolean;
}

export const STREAM_QUALITIES: { id: StreamQuality; label: string; preset: VideoPreset }[] = [
  { id: '720p30', label: '720p · 30 fps', preset: new VideoPreset(1280, 720, 2_500_000, 30) },
  { id: '1080p30', label: '1080p · 30 fps', preset: new VideoPreset(1920, 1080, 4_000_000, 30) },
  { id: '1080p60', label: '1080p · 60 fps', preset: new VideoPreset(1920, 1080, 6_000_000, 60) },
];

const GO_LIVE_PREF = 'voice.goLive';
export function lastGoLiveOptions(max: StreamQuality): GoLiveOptions {
  const allowed = STREAM_QUALITIES.slice(0, STREAM_QUALITIES.findIndex((q) => q.id === max) + 1);
  let saved: Partial<GoLiveOptions> = {};
  try {
    saved = JSON.parse(localStorage.getItem(GO_LIVE_PREF) ?? '{}') as Partial<GoLiveOptions>;
  } catch {
    /* ignore */
  }
  const fallback = allowed.some((q) => q.id === '1080p30') ? '1080p30' : allowed.at(-1)!.id;
  return {
    quality: allowed.some((q) => q.id === saved.quality) ? saved.quality! : fallback,
    content: saved.content === 'motion' ? 'motion' : 'detail',
    audio: saved.audio ?? true,
  };
}

/** Browsers that can share a screen at all (most phones can't). */
export const canShareScreen = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

export async function startScreenShare(opts: GoLiveOptions) {
  if (!room) return;
  try {
    localStorage.setItem(GO_LIVE_PREF, JSON.stringify(opts));
  } catch {
    /* ignore */
  }
  const { preset } = STREAM_QUALITIES.find((q) => q.id === opts.quality) ?? STREAM_QUALITIES[1]!;
  const lp = room.localParticipant;
  try {
    // Changing quality while live: stop the old share first (the browser asks again).
    if (useVoice.getState().screen) await lp.setScreenShareEnabled(false);
    await lp.setScreenShareEnabled(
      true,
      {
        audio: opts.audio,
        systemAudio: opts.audio ? 'include' : 'exclude',
        resolution: preset.resolution,
        contentHint: opts.content,
        selfBrowserSurface: 'exclude',
        surfaceSwitching: 'include',
      },
      {
        screenShareEncoding: preset.encoding,
        screenShareSimulcastLayers:
          preset.height > 720
            ? [ScreenSharePresets.h360fps15, ScreenSharePresets.h720fps15]
            : [ScreenSharePresets.h360fps15],
        degradationPreference:
          opts.content === 'motion' ? 'maintain-framerate' : 'maintain-resolution',
      },
    );
    useVoice.setState({ screen: true });
    report({ selfStream: true });
  } catch (err) {
    // Cancelling the browser's picker lands here; that's not an error worth showing.
    if (!(err instanceof Error && /Permission denied|NotAllowed|AbortError/i.test(err.message))) {
      useVoice.setState({ error: err instanceof Error ? err.message : 'Screen share failed' });
    }
  }
  bump();
}

export async function stopScreenShare() {
  if (!room) return;
  await room.localParticipant.setScreenShareEnabled(false).catch(() => {});
  useVoice.setState({ screen: false });
  report({ selfStream: false });
  bump();
}

/* ------------------------------------------------------------ connecting */

async function connect(
  kind: 'guild' | 'dm',
  guildId: string | null,
  channelId: string,
  join: () => Promise<VoiceJoinResponse>,
  e2ee?: { keyProvider: MlsCallKeyProvider },
) {
  const options: RoomOptions = {
    adaptiveStream: true,
    dynacast: true,
    audioCaptureDefaults: {
      deviceId: pref(PREF_IN),
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    audioOutput: { deviceId: pref(PREF_OUT) },
    ...(e2ee ? { e2ee: { keyProvider: e2ee.keyProvider, worker: new E2EEWorker() } } : {}),
  };
  const r = new Room(options);
  room = r;
  r.on(RoomEvent.TrackPublished, (pub, participant) => {
    syncSubscription(pub, participant);
    bump();
  })
    .on(RoomEvent.TrackUnpublished, (pub, participant) => {
      // The stream we were watching ended.
      if (
        pub.source === Track.Source.ScreenShare &&
        useVoice.getState().watching === participant.identity
      )
        watchStream(null);
      bump();
    })
    .on(RoomEvent.ParticipantDisconnected, (participant) => {
      if (useVoice.getState().watching === participant.identity) watchStream(null);
    })
    .on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
      attachAudio(track, pub, participant);
      bump();
    })
    .on(RoomEvent.TrackUnsubscribed, (track, pub) => {
      track.detach().forEach((el) => el.remove());
      removeAudio(pub.trackSid);
      bump();
    })
    .on(RoomEvent.LocalTrackSubscribed, (pub) => {
      if (pub.trackSid) reachedOthers.add(pub.trackSid);
    })
    .on(RoomEvent.LocalTrackPublished, (pub) => {
      if (pub.source === Track.Source.Microphone) {
        void applyLeveler();
        watchMicrophone();
      }
      bump();
    })
    .on(RoomEvent.LocalTrackUnpublished, (pub) => {
      if (pub.source === Track.Source.ScreenShare) {
        useVoice.setState({ screen: false });
        report({ selfStream: false });
      }
      bump();
    })
    .on(RoomEvent.TrackMuted, bump)
    .on(RoomEvent.TrackUnmuted, bump)
    .on(RoomEvent.ActiveSpeakersChanged, (speakers) =>
      useVoice.setState({ speaking: speakers.map((p) => p.identity) }),
    )
    .on(RoomEvent.ParticipantEncryptionStatusChanged, (enabled, participant) => {
      if (participant === r.localParticipant) useVoice.setState({ encrypted: enabled });
    })
    .on(RoomEvent.EncryptionError, (err) => {
      console.warn('call encryption error', err);
      onDecryptError();
    })
    .on(RoomEvent.AudioPlaybackStatusChanged, () =>
      useVoice.setState({ audioBlocked: !r.canPlaybackAudio }),
    )
    .on(RoomEvent.ParticipantPermissionsChanged, () => {
      // Server mute: the SFU revoked our microphone (TrackSource.MICROPHONE = 2 in the LiveKit protocol).
      const sources = r.localParticipant.permissions?.canPublishSources as number[] | undefined;
      const canMic = sources ? sources.includes(2) : true;
      if (!canMic) useVoice.setState({ muted: true });
      bump();
    })
    .on(RoomEvent.Reconnecting, () => useVoice.setState({ status: 'reconnecting' }))
    .on(RoomEvent.Reconnected, () => useVoice.setState({ status: 'connected' }))
    .on(RoomEvent.Disconnected, () => {
      if (room !== r) return;
      room = null;
      stopKeyUpdates?.();
      stopKeyUpdates = null;
      cleanupAudio();
      useVoice.setState(IDLE);
    });

  const { url, token } = await join();
  playbackReady = !isAndroidApp();
  heldAudio = [];
  playbackRestarts = 0;
  if (e2ee) await r.setE2EEEnabled(true);
  // DM calls are small and private: receive everything. Server channels choose (see syncSubscription).
  await r.connect(url, token, { autoSubscribe: kind === 'dm' });
  useVoice.setState({
    status: 'connected',
    kind,
    guildId,
    channelId,
    audioBlocked: !r.canPlaybackAudio,
  });
  syncAllSubscriptions();
  const { muted, deafened } = useVoice.getState();
  try {
    await startMicrophone(r, !muted && !deafened);
  } catch (err) {
    // No microphone or permission denied: stay connected as a listener.
    useVoice.setState({
      muted: true,
      error:
        err instanceof Error ? `Microphone unavailable: ${err.message}` : 'Microphone unavailable',
    });
  } finally {
    if (room === r) releaseHeldAudio();
  }
  report({ selfMute: useVoice.getState().muted, selfDeaf: deafened });
  bump();
}

/**
 * Joining a call. In the Android app the microphone opens even when joining muted
 * (published muted, so nothing is sent): see playbackReady.
 */
async function startMicrophone(r: Room, on: boolean) {
  if (on || !isAndroidApp()) {
    await r.localParticipant.setMicrophoneEnabled(on);
    return;
  }
  const track = await createLocalAudioTrack(r.options.audioCaptureDefaults);
  await track.mute();
  await r.localParticipant.publishTrack(track, { source: Track.Source.Microphone });
}

async function begin(
  kind: 'guild' | 'dm',
  guildId: string | null,
  channelId: string,
  run: () => Promise<void>,
) {
  if (room && useVoice.getState().channelId === channelId) return;
  await leaveVoice();
  useVoice.setState({ ...IDLE, status: 'connecting', kind, channelId, guildId, error: null });
  try {
    await run();
  } catch (err) {
    const r = room;
    room = null;
    stopKeyUpdates?.();
    stopKeyUpdates = null;
    await r?.disconnect();
    useVoice.setState({
      ...IDLE,
      error: err instanceof Error ? err.message : 'Could not connect',
    });
  }
}

export function joinVoice(guildId: string, channelId: string) {
  return begin('guild', guildId, channelId, () =>
    connect('guild', guildId, channelId, () =>
      api<VoiceJoinResponse>('POST', `/channels/${channelId}/voice/join`),
    ),
  );
}

/** Start or join an end-to-end encrypted call in a DM / group DM. */
export function joinCall(channelId: string, opts: { video?: boolean } = {}) {
  return begin('dm', null, channelId, async () => {
    if (!isE2EESupported()) {
      throw new Error(
        "This browser can't do end-to-end encrypted calls. Try a current Chrome, Edge, Firefox or Safari.",
      );
    }
    const engine = getEngine();
    if (!engine)
      throw new Error('Encryption is still starting on this device; try again in a moment');
    const first = await engine.callKey(channelId);
    if (!first) {
      throw new Error(
        "This device doesn't have this conversation's encryption keys yet, so it can't join an encrypted call.",
      );
    }
    const keyProvider = new MlsCallKeyProvider();
    await keyProvider.setCallKey(first.key, first.epoch);

    // Rotate the call key whenever the conversation's MLS epoch changes.
    const updateKey = () =>
      engine.callKey(channelId).then((k) => {
        useVoice.setState({ keysUnavailable: !k });
        return k && keyProvider.setCallKey(k.key, k.epoch);
      });
    const off = engine.on((e) => {
      if (e.type !== 'epoch' || e.channelId !== channelId) return;
      void updateKey();
    });
    // callKey() syncs the conversation first, so this also catches up on missed commits.
    refreshKey = async () => void (await updateKey());
    stopKeyUpdates = () => {
      off();
      refreshKey = null;
    };

    await connect(
      'dm',
      null,
      channelId,
      () => api<VoiceJoinResponse>('POST', `/channels/${channelId}/call/join`),
      {
        keyProvider,
      },
    );
    if (opts.video) {
      await micReachedOthers();
      await toggleCamera();
    }
  });
}

export async function declineCall(channelId: string) {
  await api('POST', `/channels/${channelId}/call/decline`).catch(() => {});
}

export async function leaveVoice() {
  const r = room;
  room = null;
  stopKeyUpdates?.();
  stopKeyUpdates = null;
  cleanupAudio();
  if (r && r.state !== ConnectionState.Disconnected) await r.disconnect();
  useVoice.setState(IDLE);
}

/* -------------------------------------------------------------- controls */

/**
 * Joining someone's video call: let the microphone get going before the camera. With the
 * other side's tracks arriving at the same moment, publishing both at once sometimes left
 * the new connection stuck for ~10 s (the server timed it out and it reconnected).
 */
async function micReachedOthers(timeoutMs = 4000) {
  const r = room;
  if (!r || r.remoteParticipants.size === 0) return;
  const pub = r.localParticipant.getTrackPublication(Track.Source.Microphone);
  if (!pub?.trackSid || reachedOthers.has(pub.trackSid)) return;
  await new Promise<void>((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      r.off(RoomEvent.LocalTrackSubscribed, onSubscribed);
      resolve();
    };
    const onSubscribed = (subscribed: LocalTrackPublication) => {
      if (subscribed.trackSid === pub.trackSid) done();
    };
    const timer = window.setTimeout(done, timeoutMs);
    r.on(RoomEvent.LocalTrackSubscribed, onSubscribed);
  });
}

/* ------------------------------------------------------- keep the mic alive */

/** Our tracks the server has started sending to someone (RoomEvent.LocalTrackSubscribed). */
const reachedOthers = new Set<string>();
let micRestartTimer: number | undefined;
let micRestarting: Promise<void> | null = null;
let watchedMic: MediaStreamTrack | null = null;

const micTrack = () =>
  room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as
    LocalAudioTrack | undefined;

/**
 * Re-open the microphone (same device and settings, same publication): phones can leave an
 * open microphone silent, or end it, when the camera starts or the call's audio output
 * changes (the Android app moves video calls to the loudspeaker).
 */
export function restartMicrophone(reason: string) {
  if (!room) return;
  window.clearTimeout(micRestartTimer);
  micRestartTimer = window.setTimeout(() => {
    micRestarting ??= reopenMicrophone(reason).finally(() => {
      micRestarting = null;
    });
  }, 500);
}

async function reopenMicrophone(reason: string) {
  const r = room;
  const track = micTrack();
  if (!r || !track) return;
  console.info(`restarting the microphone (${reason})`);
  const { muted } = useVoice.getState();
  try {
    await track.restartTrack();
    if (muted && !track.isMuted) await track.mute();
  } catch (err) {
    console.warn('microphone restart failed; starting a new one', err);
    try {
      await r.localParticipant.unpublishTrack(track).catch(() => {});
      await startMicrophone(r, !muted);
    } catch {
      useVoice.setState({ error: 'Microphone stopped. Tap the mic to restart it.' });
      return;
    }
  }
  watchMicrophone();
  void applyLeveler();
  nativeCall.reapply();
}

/** Restart the microphone if it ends, or goes silent at the source, while we're talking. */
function watchMicrophone() {
  const mst = micTrack()?.mediaStreamTrack;
  if (!mst || mst === watchedMic) return;
  watchedMic = mst;
  let silent: number | undefined;
  mst.addEventListener('ended', () => {
    if (watchedMic === mst) restartMicrophone('it ended');
  });
  mst.addEventListener('mute', () => {
    window.clearTimeout(silent);
    silent = window.setTimeout(() => {
      if (watchedMic === mst && mst.muted && !useVoice.getState().muted)
        restartMicrophone('it went silent');
    }, 1000);
  });
  mst.addEventListener('unmute', () => window.clearTimeout(silent));
}

/**
 * Turn the microphone on or off. If it won't come back on (on phones another app, a call or
 * an audio change can take it away), start a fresh one instead of staying muted.
 */
async function enableMicrophone(on: boolean) {
  const lp = room?.localParticipant;
  if (!lp) return;
  try {
    await lp.setMicrophoneEnabled(on);
  } catch (err) {
    if (!on) throw err;
    console.warn('microphone did not resume; starting a new one', err);
    const old = lp.getTrackPublication(Track.Source.Microphone)?.track;
    if (old) await lp.unpublishTrack(old).catch(() => {});
    await lp.setMicrophoneEnabled(true);
  }
}

export async function toggleMute() {
  const { muted, deafened } = useVoice.getState();
  const next = !muted;
  // Unmuting while deafened also undeafens (Discord behaviour).
  if (!next && deafened) return toggleDeafen();
  useVoice.setState({ muted: next });
  await enableMicrophone(!next).catch((err: unknown) => {
    useVoice.setState({
      muted: true,
      error: err instanceof Error ? err.message : 'Microphone unavailable',
    });
  });
  if (room) report({ selfMute: useVoice.getState().muted });
}

export async function toggleDeafen() {
  const { deafened, muted } = useVoice.getState();
  const next = !deafened;
  useVoice.setState({ deafened: next });
  for (const [sid, el] of audioEls) {
    el.muted = next;
    applyPlayback(sid);
  }
  // Deafening mutes; undeafening restores the previous mic state only if we weren't muted before.
  const nextMuted = next ? true : muted && !deafened ? true : false;
  useVoice.setState({ deafened: next, muted: nextMuted });
  await enableMicrophone(!nextMuted).catch((err: unknown) =>
    useVoice.setState({
      muted: true,
      error:
        err instanceof Error ? `Microphone unavailable: ${err.message}` : 'Microphone unavailable',
    }),
  );
  if (room) report({ selfDeaf: next, selfMute: useVoice.getState().muted });
}

export async function toggleCamera() {
  if (!room) return;
  const next = !useVoice.getState().camera;
  try {
    await room.localParticipant.setCameraEnabled(next);
    useVoice.setState({ camera: next });
    report({ selfVideo: next });
    // In the Android app, starting the camera (and the switch to the loudspeaker that comes
    // with video) can leave the microphone silent.
    if (isAndroidApp()) restartMicrophone('camera');
  } catch (err) {
    useVoice.setState({
      error: err instanceof Error ? `Camera unavailable: ${err.message}` : 'Camera unavailable',
    });
  }
  bump();
}

export async function listDevices(kind: MediaDeviceKind) {
  return Room.getLocalDevices(kind, true);
}

export async function setDevice(
  kind: 'audioinput' | 'audiooutput' | 'videoinput',
  deviceId: string,
) {
  try {
    localStorage.setItem(
      kind === 'audioinput' ? PREF_IN : kind === 'audiooutput' ? PREF_OUT : 'voice.videoDevice',
      deviceId,
    );
  } catch {
    /* ignore */
  }
  await room?.switchActiveDevice(kind, deviceId);
}

export const clearVoiceError = () => useVoice.setState({ error: null });
